<template>
  <v-container>
    <v-row align="baseline" dense>
      <v-col cols="12" class="text-right">
        <v-btn
          color="primary"
          :to="{ name: 'tenant.create' }"
          v-if="route.meta.type === 'tenant'"
        >
          Novo Cliente
        </v-btn>
        <v-btn v-else color="primary" :to="{ name: 'company.create' }">
          Nova empresa
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-text-field
          label="Buscar por Razão Social ou CNPJ"
          v-model="searchTerm"
          clearable
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row align="start">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="companies"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :items-length="totalItems"
          :items-per-page-options="[
            { value: 10, title: '10' },
            { value: 20, title: '20' },
            { value: 40, title: '40' },
            { value: 50, title: '50' },
          ]"
          :loading="isLoading"
          @click:row="onCompanyClicked"
        >
<template v-slot:item.cnpj="{ item }">
            {{ formatCnpj(item.cnpj) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div
              class="d-inline-flex"
              @click.stop="onEditCompany(item)"
              v-if="$can('company:update')"
            >
              <v-icon :icon="mdiPencil" />
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, inject } from "vue";
import { mdiPencil } from "@mdi/js";
import { useRoute, useRouter } from "vue-router";
import { useCompaniesQuery } from "@/queries/modules/companies";
import { useSnackbar } from "@/plugins/snackbar";

const route = useRoute();
const router = useRouter();
const $can = inject("$can");
const $snackbar = useSnackbar();

const searchTerm = ref("");
const searchDebounceTimer = ref(null);
const page = ref(1);
const itemsPerPage = ref(10);

const headers = [
  { title: "Nome", key: "razao_social", sortable: false },
  { title: "CNPJ", key: "cnpj", sortable: false },
{ title: "", key: "actions", sortable: false },
];

// Computed query params
const queryParams = computed(() => {
  const params = {
    page: page.value,
    per_page: itemsPerPage.value,
    tenant_id: route.params.tenant_id,
  };

  // Add search parameters if search term is present
  if (searchTerm.value) {
    params.razao_social = searchTerm.value;
    params.cnpj = searchTerm.value;
  }

  return params;
});

// TanStack Query for companies
const { data, isLoading, isError, error } = useCompaniesQuery(queryParams);

// Extract data from query response
const companies = computed(() => data.value?.items || []);
const totalItems = computed(() => data.value?.total || 0);

// Format CNPJ (replaces the filter)
const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

const onCompanyClicked = (_, { item }) => {
  if (route.meta.type === "tenant") {
    router.push({
      name: "tenant.billing",
      params: {
        tenant_id: item.id,
      },
    });
  } else {
    router.push({
      name: "company.edit",
      params: {
        company_id: item.id,
        tenant_id: route.params.tenant_id,
      },
    });
  }
};

const onEditCompany = (item) => {
  if (route.meta.type === "tenant") {
    router.push({
      name: "tenant.edit",
      params: {
        tenant_id: item.id,
      },
    });
  } else {
    router.push({
      name: "company.edit",
      params: {
        company_id: item.id,
        tenant_id: route.params.tenant_id,
      },
    });
  }
};

const debouncedSearch = () => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
  searchDebounceTimer.value = setTimeout(() => {
    page.value = 1; // Reset to first page on search
  }, 500);
};

// Watch for search term changes
watch(searchTerm, () => {
  debouncedSearch();
});

// Watch for errors
watch(isError, (newValue) => {
  if (newValue && error.value) {
    $snackbar.error("Ocorreu um erro ao listar os clientes");
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
});
</script>
